import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { firestore } from "/firebaseConfig";
import { normalizeBandOffConfig, normalizeBandOffSongs } from "@/utils/bandOff/schema";

function getBoardRef(boardId) {
  if (!boardId) {
    throw new Error("boardId가 필요합니다.");
  }
  return doc(firestore, "boards", boardId);
}

function getSongsCollection(boardId) {
  return collection(firestore, "boards", boardId, "songs");
}

function getEntriesCollection(boardId) {
  return collection(firestore, "boards", boardId, "entries");
}

function isRecruitmentOpen(config) {
  if (config.recruitStatus !== "open") return false;
  const now = Date.now();
  const normalizeDate = (value) => {
    if (!value) return null;
    if (value?.toDate) return value.toDate().getTime();
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  };
  const start = normalizeDate(config.recruitStartAt);
  const end = normalizeDate(config.recruitEndAt);
  if (start && now < start) return false;
  if (end && now > end) return false;
  return true;
}

async function getActiveEntries(boardId) {
  const q = query(getEntriesCollection(boardId), where("status", "==", "active"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function syncBandOffSongs(boardId, songs) {
  const normalizedSongs = normalizeBandOffSongs(songs);
  const songsRef = getSongsCollection(boardId);
  const existingSnapshot = await getDocs(songsRef);
  const batch = writeBatch(firestore);

  existingSnapshot.docs.forEach((songDoc) => {
    batch.delete(songDoc.ref);
  });

  normalizedSongs.forEach((song) => {
    const newDocRef = doc(songsRef);
    batch.set(newDocRef, {
      ...song,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });

  await batch.commit();
}

export async function getBandOffSongs(boardId) {
  const snapshot = await getDocs(getSongsCollection(boardId));
  return snapshot.docs
    .map((songDoc) => ({ id: songDoc.id, ...songDoc.data() }))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

export async function getBandOffEntries(boardId) {
  return getActiveEntries(boardId);
}

export async function createBandOffEntry({ boardId, songId, songTitle, part, user }) {
  const boardRef = getBoardRef(boardId);
  const entriesRef = getEntriesCollection(boardId);
  const createdAt = Timestamp.now();

  await runTransaction(firestore, async (transaction) => {
    const boardSnapshot = await transaction.get(boardRef);
    if (!boardSnapshot.exists()) {
      throw new Error("게시글을 찾을 수 없습니다.");
    }

    const boardData = boardSnapshot.data();
    const bandOffConfig = normalizeBandOffConfig(boardData.bandOffConfig || {});

    if (boardData.type !== "bandOff" || boardData.category !== "ggookoff") {
      throw new Error("꾹오프 글이 아닙니다.");
    }

    if (!isRecruitmentOpen(bandOffConfig)) {
      throw new Error("현재 모집 가능한 상태가 아닙니다.");
    }

    const activeEntriesSnapshot = await transaction.get(
      query(entriesRef, where("status", "==", "active"))
    );
    const activeEntries = activeEntriesSnapshot.docs.map((entryDoc) => entryDoc.data());

    const duplicated = activeEntries.some(
      (entry) => entry.songId === songId && entry.userId === user.email
    );
    if (duplicated) {
      throw new Error("같은 곡에는 1번만 엔트리할 수 있습니다.");
    }

    const activeUserIds = new Set(activeEntries.map((entry) => entry.userId).filter(Boolean));
    if (!activeUserIds.has(user.email)) {
      const maxParticipants = bandOffConfig.maxParticipants || 0;
      if (maxParticipants > 0 && activeUserIds.size >= maxParticipants) {
        throw new Error("총 참가 인원 제한에 도달했습니다.");
      }
    }

    const partLimit = (bandOffConfig.partLimits && bandOffConfig.partLimits[part]) || 0;
    if (partLimit > 0) {
      const currentPartCount = activeEntries.filter(
        (entry) => entry.songId === songId && entry.part === part
      ).length;
      if (currentPartCount >= partLimit) {
        throw new Error("해당 파트 인원 제한에 도달했습니다.");
      }
    }

    const newEntryRef = doc(entriesRef);
    transaction.set(newEntryRef, {
      songId,
      songTitle,
      part,
      userId: user.email,
      userNickname: user.nickname || user.email,
      status: "active",
      createdAt,
      updatedAt: createdAt,
    });

    const nextParticipants = activeUserIds.has(user.email)
      ? activeUserIds.size
      : activeUserIds.size + 1;

    transaction.update(boardRef, {
      bandOffConfig: {
        ...bandOffConfig,
        currentParticipants: nextParticipants,
      },
      updatedAt: createdAt,
    });
  });
}

export async function cancelBandOffEntry({ boardId, entryId, userEmail, isAdmin }) {
  const boardRef = getBoardRef(boardId);
  const entryRef = doc(firestore, "boards", boardId, "entries", entryId);
  const now = Timestamp.now();

  await runTransaction(firestore, async (transaction) => {
    const [boardSnapshot, entrySnapshot] = await Promise.all([
      transaction.get(boardRef),
      transaction.get(entryRef),
    ]);

    if (!boardSnapshot.exists() || !entrySnapshot.exists()) {
      throw new Error("엔트리 정보를 찾을 수 없습니다.");
    }

    const boardData = boardSnapshot.data();
    const bandOffConfig = normalizeBandOffConfig(boardData.bandOffConfig || {});
    const entryData = entrySnapshot.data();

    if (!isAdmin && entryData.userId !== userEmail) {
      throw new Error("엔트리 취소 권한이 없습니다.");
    }

    transaction.update(entryRef, {
      status: "cancelled",
      cancelledAt: now,
      updatedAt: now,
    });

    const activeEntriesSnapshot = await transaction.get(
      query(getEntriesCollection(boardId), where("status", "==", "active"))
    );
    const activeEntries = activeEntriesSnapshot.docs
      .filter((item) => item.id !== entryId)
      .map((item) => item.data());
    const activeUserIds = new Set(activeEntries.map((entry) => entry.userId).filter(Boolean));

    transaction.update(boardRef, {
      bandOffConfig: {
        ...bandOffConfig,
        currentParticipants: activeUserIds.size,
      },
      updatedAt: now,
    });
  });
}

export async function createBandOffPostArtifacts(boardId, bandOffConfig, songs) {
  const normalizedConfig = normalizeBandOffConfig(bandOffConfig);
  const boardRef = getBoardRef(boardId);

  await runTransaction(firestore, async (transaction) => {
    const boardSnapshot = await transaction.get(boardRef);
    if (!boardSnapshot.exists()) return;
    transaction.update(boardRef, {
      type: "bandOff",
      bandOffConfig: normalizedConfig,
      updatedAt: Timestamp.now(),
    });
  });

  await syncBandOffSongs(boardId, songs);
}

export async function deleteBandOffSong(boardId, songId) {
  await deleteDoc(doc(firestore, "boards", boardId, "songs", songId));
}

export async function addBandOffSong(boardId, song) {
  const normalizedSongs = normalizeBandOffSongs([song]);
  if (normalizedSongs.length === 0) {
    throw new Error("곡 제목을 입력해주세요.");
  }
  const songsRef = getSongsCollection(boardId);
  await addDoc(songsRef, {
    ...normalizedSongs[0],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
